/* eslint no-eval: 0 */
import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import withTranslations from '../../../translations';

import {
  Collapse,
  ExpansionPanel,
  ExpansionPanelSummary,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  Paper,
  Select,
  SuperSelect,
  TextField,
  Typography,
  Button
} from '../../base';

import FileInput from '../../FileInput/FileInput';
import SearchIcon from '@material-ui/icons/Search';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { getSAPCauseCodes } from '../../../constants/urls';
import { QuestionSectionNames } from '../../../views/SingleJob/constants';

const styles = theme => ({
  root: {
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    border: 'none',

    '&.standardForm' : {
      maxWidth: 'calc(100vw - 32px)',
      margin: `0 -${theme.spacing(0.5)}px ${theme.spacing(3)}px`,
      boxShadow: theme.shadows[1],
      paddingBottom: 0,

      '&.Mui-expanded': {
        marginBottom: theme.spacing(3),
      },

      [theme.breakpoints.up('sm')]: {
        margin: theme.spacing(0, 0, 3),
      }
    },
  },
  title: {
    maxWidth: 'calc(100vw - 32px)',
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    borderBottom: '2px solid rgba(0, 0, 0, 0.1)',

    '&.standardForm': {
      background: '#f3f3f3',
      border: 'none',
      margin: 0,
      padding: theme.spacing(0, 3),

      '&.Mui-expanded': {
        borderBottom: '1px inset rgba(0, 0, 0, 0.2)',
      }
    },
  },
  subsectionBody: {
    padding: theme.spacing(1.5, 1, 3),
  },
  orphanage: {
    marginBottom: theme.spacing(3)
  },
  subsubsectionTitle: {
    marginTop: theme.spacing(),
    paddingTop: theme.spacing(3),
    borderTop: '1px solid #dedede',
  },
  field: {
    margin: `${theme.spacing()}px ${theme.spacing(0.25)}px`,

    '&.indented': {
      marginLeft: theme.spacing(2),
    },

    '& .MuiTypography-colorTextSecondary': {
      color: 'rgba(0, 0, 0, 0.54)',
    },

    '& label': {
      fontSize: '1rem',
    },

    [theme.breakpoints.up('sm')]: {
      margin: `${theme.spacing()}px ${theme.spacing(2)}px`,

      '&.indented': {
        marginLeft: theme.spacing(3),
      },
    },
  },
  unansweredField: {
    margin: `${theme.spacing()}px ${theme.spacing(0.25)}px`,

    '&.indented': {
      marginLeft: theme.spacing(2),
    },

    '& label': {
      fontSize: '1rem',

      '& p': {
        color: theme.palette.error.light,
      },
    },

    [theme.breakpoints.up('sm')]: {
      margin: `${theme.spacing()}px ${theme.spacing(2)}px`,

      '&.indented': {
        marginLeft: theme.spacing(3),
      },
    },
  },
  selectField: {
    marginTop: theme.spacing(2),
  },
  unansweredSelectField: {
    marginTop: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.error.main}`,
  },
  unansweredTextField : {
    borderBottom: `1px solid ${theme.palette.error.main}`,

    '& label': {
      color: theme.palette.error.light,
    },
  },
  selectLabel: {
    marginTop: theme.spacing(),
    top: -10,
  },
  superSelectField: {
    marginTop: theme.spacing(3),
  },
  superSelectLabel: {
    position: 'absolute',
    color: theme.palette.text.hint,
    fontSize: 12,
    top: 0,
  },
  error: {
    position: 'absolute',
    left: 0,
    bottom: -20,
    minHeight: theme.spacing(2),
    color: theme.palette.secondary.main,
    fontSize: theme.typography.fontSize * 0.75,
    marginTop: theme.spacing(0.5),
  },
  validationError: {
    color: theme.palette.error.light,
  },
  formButton: {
    color: theme.palette.secondary.main,
    float: 'right',
  },
});

class FormSection extends Component {
  state = {
    localValues: {},
  };

  // This is specific for addJob form
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.answers.serviceTechnologyCenterPlannerGroup !== this.props.answers.serviceTechnologyCenterPlannerGroup) {
      if (this.state.localValues.materialNumberSearch) {
        this.setState(prevState => ({
          localValues: Object.assign({}, prevState.localValues, {materialNumberSearch: null})
        }))
      }
    }
  }

  handleLocalInput = field => e => {
    const key = field.key;
    const value = e.target.value;

    this.setState(prevState => ({
      localValues: Object.assign({}, prevState.localValues, {[key]: value})
    }))

    // If there is an error connected to search field, re-validate on each input
    if (this.props.errors[key]) this.props.validateField(field, value);
  }

  handleInput = key => e => {
    this.props.handleFieldChange(key, e.target.value || null, null, false)
  }

  handleFaultyPartsRecipientMailsListInput = e => {
    const value = e.target.value;
    this.setState(prevState => ({
      localValues: Object.assign({}, prevState.localValues, { faultyPartsRecipientMailsList: value })
    }))
  }

  handleSelect = (field, isSuper = false) => async e => {
    let answer = isSuper ? e.value : e.target.value;

    if (field.key === 'StripObservation_SAPCauseCodeGroup') {
      this.props.sendAnswerAction(getSAPCauseCodes(this.props.id), answer , true)
    } 

    this.handleAnswerWithApiEndpoint(field, answer);
  }

  // This is a specific field in addJob form. It should check if there is an error
  // before sending an API call
  handleSearch = async field => {
    const { key } = field;
    const currentValue = this.state.localValues[key];
    const isValid = currentValue && await this.props.validateField(field, currentValue.trim());

    if (isValid && currentValue && currentValue.trim()) {
      this.handleAnswerWithApiEndpoint(field, currentValue.trim());
    }
  }

  handleAnswerWithApiEndpoint = async (field, answer) => {
    const { key, apiEndpoint, additionalParameter, updateValueFor } = field;

    await this.props.handleFieldChange(key, answer, field);

    // If a field has apiEndpoint value, send answer to API
    if (apiEndpoint && answer) {
      if (additionalParameter) {
        answer = encodeURIComponent(answer);
        answer = `${this.props.answers[additionalParameter]}/${answer}`;
      }

      this.props.sendAnswerAction(apiEndpoint, key === 'reportLevel' ? '' : answer);
    }

    // Handle if field should update value for other field
    if (updateValueFor) {
      // Very situational: if value of updateValueFor field starts with 'reset '
      // clear that field in saved answers.
      if (updateValueFor.includes('reset ')) {
        const keyToReset = updateValueFor.split('reset ')[1];

        if (this.props.answers[keyToReset]) {
          this.props.handleFieldChange(keyToReset, null);
        }

      // Else get the description
      } else {
        this.getDescriptionForAutoField(updateValueFor, key);
      }
    }
  }

  handleSendFaultyPartsEmails = async () => {
    const { faultyPartsRecipientMailsList: emails } = this.state.localValues
    const packedData = {
      emails: emails.split('\n').map(e => e.trim()),
    };
    await this.props.submitForm();
    await this.props.sendFaultyPartsEmailsAction(this.props.id, packedData);
  }

  // Render helper text only on add job form
  renderIsRequired = (isRequired, currentValue, key, shouldHide) => {
    const { classes: c, t } = this.props;
    const isRequiredTranslation = t.Forms.errors.required;
    const hasCurrentValue = currentValue || (Array.isArray(currentValue) && !currentValue.length)

    return (
      this.props.formType === 'addJob' &&
      !shouldHide &&
      !this.props.errors[key] &&
      <div className={c.error}>{isRequired === true && !hasCurrentValue && isRequiredTranslation}</div>
    )
  }

  getDescriptionForAutoField = (key, auto) => {
    if (auto) {
      const autoFieldValue = this.props.answers[auto];
      const autoFieldOptions = this.props.options[auto];
      const valueFromAutoField =
              autoFieldOptions
              && autoFieldValue
              && autoFieldOptions.find(opt => opt.value === autoFieldValue);
      const descriptionFromAutoField = valueFromAutoField && valueFromAutoField.description;
      const value = descriptionFromAutoField || autoFieldValue || null;

      this.props.handleFieldChange(key, value, null, false);
    }
  }

  generateUnsavedSessionFilesData = (jobId, section, key) => {
    const questionAnswerSession = JSON.parse(sessionStorage.getItem('questionAnswerSession'));
    if (
      questionAnswerSession &&
      questionAnswerSession[jobId] &&
      questionAnswerSession[jobId][section] &&
      questionAnswerSession[jobId][section].files
    ) {
      return questionAnswerSession[jobId][section].files.filter(file => file.location === key);
    }
    return [];
  }

  renderSelectField = field => {
    const { key, shouldHide, type, label, options, required, optionsFrom } = field;
    const { classes: c, answers, } = this.props;
    const currentValue = answers[key];
    // Options received from another question (via API call)
    const specialOptions = this.props.options && this.props.options[key];
    // If field responsible for specialOptions has no value, override specialOptions with an empty array.
    const resetOptionsIfNoValueForParent = optionsFrom && !answers[optionsFrom] ? [] : specialOptions;
    // If no specialOptions, use regular `options` from the question.
    const superSelectOptions = specialOptions ? resetOptionsIfNoValueForParent : options;
    // Find answer (currentValue) in the options so we can find and display its `label` (or `value` if `label` is missing)
    const superSelectSelectedValue = superSelectOptions && superSelectOptions.find(opt => opt.value === currentValue);
    const superSelectLabel = superSelectSelectedValue
                              ? superSelectSelectedValue.label ? {'label': superSelectSelectedValue.label} : {'label': superSelectSelectedValue.value}
                              : null;
    // Field should be disabled if:
    // 1) There are no options nor specialOptions, OR
    // 2) There are specialOptions but the field responsible for specialOptions has no answer.
    const isDisabled = (options && !options.length && !specialOptions)
                    || (specialOptions && optionsFrom && !answers[optionsFrom]);

    const unansweredClass = currentValue ? c.selectField : this.props.formType === 'addJob' ? c.selectField : c.unansweredSelectField;

    // Clear answers for select fields without options
    if (superSelectOptions && !superSelectOptions.length) this.props.handleFieldChange(key, null, field)

    return (
      <Collapse in={!shouldHide || !!required}>
        { field.condition && this.props.formType === 'addJob'
        ? <div className={c.superSelectField}>
            {currentValue ? <Typography variant='caption' className={c.superSelectLabel}>{label}</Typography> : null}
            <SuperSelect
              className={unansweredClass}
              menuPortalTarget={document.getElementById('portal-for-SuperSelect')}
              styles={{ menuPortal: styles => ({ ...styles, zIndex: 1000 }) }}
              isMulti={type === 'multiselect'}
              defaultValue={[]}
              value={superSelectLabel}
              placeholder={label}
              options={superSelectOptions}
              onChange={this.handleSelect(field, true)}
              isDisabled={isDisabled}
            />
          </div>
        : <Select
            hasNullOption={required !== true}
            className={unansweredClass}
            fullWidth
            multiple={type === 'multiselect'}
            required={required === true}
            options={specialOptions ? resetOptionsIfNoValueForParent : options || []}
            onChange={this.handleSelect(field)}
            value={currentValue || (type === 'multiselect' ? [] : '')}
            disabled={isDisabled}
          />
        }
        {this.renderIsRequired(required, currentValue, key)}
    </Collapse>
    );
  }

  sendNotifyTeamEmails = async () => {
    await this.props.submitForm();
    await this.props.faultyPartsNotifyTeamAction(this.props.id);
  }

  renderNotifyTeam = () => {
    const { classes: c, t, answers } = this.props;
    return (
    <Button
      className={classNames(c.formButton)}
      onClick={this.sendNotifyTeamEmails}
      disabled={!answers['StripObservation_FaultyParts_PartsRequestedForReturn']}
    >
      <Typography variant='caption'>{t.Forms.fields.notifyTeam}</Typography>
    </Button>
    );
  }

  renderProperFormField = (field, error) => {
    const { type, control, label, key, required, auto, fields, shouldHide, maxLength, minLength } = field;
    const {
      classes: c,
      gt,
      answers,
      errors,
      uploadEndpoint,
      rotateImageEndpoint,
      formName,
      isLocked,
      shouldSaveForm,
      user,
    } = this.props;
    const errorTranslations = gt.Forms.errors;
    const currentValue = answers[key];
    const unansweredClass = !currentValue && this.props.formType !== 'addJob' ? c.unansweredTextField : null;

    switch (type) {
      case 'orphanage':
        return (
          <div>
            {fields && this.renderFields(fields, errors)}
          </div>
        );
      case 'subsection':
        return (
          <Collapse in={!shouldHide}>
            <div className={c.subsubsectionTitle}>{label}</div>
            {fields && this.renderFields(fields, errors)}
          </Collapse>
        );
      case 'input':
        return (
          <Collapse in={!shouldHide || !!required}>
            <TextField
              className={unansweredClass}
              fullWidth
              type={control === 'number' ? 'text' : control}
              label={label}
              onChange={this.handleInput(key)}
              value={currentValue || ''}
              error={!!error}
            />
            {this.renderIsRequired(required, currentValue, key, shouldHide)}
            {error &&
             <span className={classNames(c.error, c.validationError)}>
              {currentValue && currentValue.length > maxLength ? errorTranslations['maxLength'](maxLength) : errorTranslations[control]}
            </span>}
          </Collapse>
        );
      case 'search':
        return (
          <Collapse in={!shouldHide || !!required}>
            <FormControl fullWidth>
              <InputLabel htmlFor={key}>{label}</InputLabel>
              <Input
                id={key}
                type={control === 'number' ? 'text' : control}
                onChange={this.handleLocalInput(field)}
                value={this.state.localValues[key] || ''}
                error={!!error}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton onClick={() => this.handleSearch(field)}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
            {this.renderIsRequired(required, currentValue, key)}
            {error &&
             <span className={classNames(c.error, c.validationError)}>
              {this.state.localValues[key] && this.state.localValues[key].length < minLength
               ? errorTranslations['minLength'](minLength)
               : errorTranslations[control]}
             </span>}
          </Collapse>
        );
      case 'select':
      case 'multiselect':
        return this.renderSelectField(field);
      case 'textarea':
        // If textarea field has auto value, it should be disabled
        // and populated with text derived from the value selected in the 'auto' field
        return (
          <Collapse in={!shouldHide || !!required}>
            <TextField
              className={unansweredClass}
              fullWidth
              disabled={!!auto}
              rowsMax={10}
              multiline
              label={label}
              onChange={this.handleInput(key)}
              value={(this.props.options && this.props.options[key]) || currentValue || ''}
            />
            {this.renderIsRequired(required, currentValue, key)}
            {key === 'StripObservation_FaultyParts_PartsRequestedForReturn' && this.renderNotifyTeam()}
          </Collapse>
        );
      case 'file':
        return (
          <FileInput
            user={user}
            field={field}
            formName={formName}
            error={error}
            uuids={field.answer ? field.answer : []}
            files={
              field.files
                ? field.files.concat(this.generateUnsavedSessionFilesData(this.props.id, field.section, field.key))
                : []
            }
            handleFieldChange={this.props.handleFieldChange}
            renderIsRequired={this.renderIsRequired}
            uploadEndpoint={uploadEndpoint}
            rotateImageEndpoint={rotateImageEndpoint}
            isDisabled={isLocked && formName !== QuestionSectionNames.CONFORMITY_TEST}
            shouldSaveForm={shouldSaveForm}
            isLocked={isLocked}
          />
        );
      default:
        return 'Missing field type. Supported types are: input, select, multiselect, file, search, and textarea';
    }
  }

  generateNonStandardControls = subsectionKey => {
    const { classes: c, t, answers } = this.props;

    switch (subsectionKey) {
      case 'StripObservation_FaultyParts':
        const { faultyPartsRecipientMailsList: emails } = this.state.localValues;

        function isEnabledFaultyPartsListTextArea() {
          return (
            answers['StripObservation_FaultyParts_DescriptionBox'] &&
            answers['StripObservation_FaultyParts_FaultyPartsDescriptionWear']
          );
        }

        function isEnabledSendEmailsButton() {
          return (
            isEnabledFaultyPartsListTextArea() &&
            listContainsAllValidEmailAddresses()
          );
        }

        function listContainsAllValidEmailAddresses() {
          return emails
            ? emails.split('\n').reduce((a, c) => a && (/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g).test(c), true)
            : false;
        }

        return (
          <FormControl key="emailToList" fullWidth>
            <Collapse in={true}>
              <div className={classNames(c.field)}>
                <TextField
                  fullWidth
                  multiline
                  rowsMax={50}
                  type='text'
                  label={t.recipientEmails}
                  onChange={this.handleFaultyPartsRecipientMailsListInput}
                  value={this.state.localValues.faultyPartsRecipientMailsList || ''}
                  disabled={!isEnabledFaultyPartsListTextArea()}
                />
                <Button
                  onClick={this.handleSendFaultyPartsEmails}
                  className={classNames(c.formButton)}
                  disabled={!isEnabledSendEmailsButton()}
                >
                  {t.send}
                </Button>
              </div>
            </Collapse>
          </FormControl>
        );
      default :
        return null;
    }
  }

  renderFields = (fields, errors, subsectionKey) => {
    let renderedFields = fields.map(field => {
      const { answers, formType, classes } = this.props
      const { key, type, label, shouldHide, required, alwaysShow } = field;
      const currentValue = answers[key];
      const error = errors[key] || false;
      const isIndented = field.level ? 'indented' : '';

      return (
        (!shouldHide || alwaysShow) &&
        <Collapse in={!shouldHide || !!required} key={key}>
          <div className={!currentValue && formType !== 'addJob'  ? classNames(classes.unansweredField, `${isIndented}`) : classNames(classes.field, `${isIndented}`)}>
            <FormControl fullWidth>
              { (type === 'select' || type === 'multiselect')
                ? <InputLabel classes={{ root: this.props.classes.selectLabel }}>
                    <Typography color='textSecondary'>{label}</Typography>
                  </InputLabel>
                : null
              }
              {this.renderProperFormField(field, error)}
            </FormControl>
          </div>
        </Collapse>
      );
    });
    

    const nonStandardControls = this.generateNonStandardControls(subsectionKey);
    if (nonStandardControls) {
      renderedFields = [
        ...renderedFields.slice(0, 2),
        nonStandardControls,
        ...renderedFields.slice(2)
      ]
    }

    return renderedFields;
  }

  renderSubsections = () => {
    const { classes: c, data, formType, errors } = this.props;
    const isStandard = formType ? '' : 'standardForm';

    return (
      isStandard
      ? <ExpansionPanel className={classNames(c.root, `${isStandard}`)}>
          <ExpansionPanelSummary
            expandIcon={<ExpandMoreIcon />}
            className={classNames(c.title, `${isStandard}`)}
          >
            <Typography variant='subtitle1'>{data.label}</Typography>
          </ExpansionPanelSummary>
          <div className={c.subsectionBody}>{this.renderFields(data.fields, errors, data.key)}</div>
        </ExpansionPanel>
      : <Paper component='fieldset' className={classNames(c.root, `${isStandard}`)}>
          <header className={classNames(c.title, `${isStandard}`)}>
            <Typography variant='subtitle1'>{data.label}</Typography>
          </header>
          <div className={c.subsectionBody}>{this.renderFields(data.fields, errors)}</div>
        </Paper>
    )
  }

  render() {
    const { classes: c, data, errors } = this.props;

    return (
      <Collapse in={!data.shouldHide}>
        {data.fields && data.type !== 'orphanage' && this.renderSubsections()}
        {data.type === 'orphanage' && <div className={c.orphanage}>{this.renderFields([data], errors)}</div>}
      </Collapse>
    );
  }
}

export default withStyles(styles)(withTranslations(FormSection, 'Global'));



// WEBPACK FOOTER //
// ./src/components/SectionedForm/FormSection/FormSection.js